<template>
  <div>
    <!-- Toolbar: Create folder button -->
    <div class="d-flex justify-end mb-2">
      <v-btn variant="outlined" size="small" :prepend-icon="mdiFolderPlus" @click="openNewFolderDialog()">
        New Folder
      </v-btn>
    </div>

    <!-- Folders -->
    <v-expansion-panels v-if="sortedFolders.length" v-model="openPanels" multiple variant="accordion" class="mb-2">
      <v-expansion-panel v-for="folder in sortedFolders" :key="folder.id" :value="folder.id">
        <v-expansion-panel-title>
          <v-icon :icon="mdiFolderOpen" class="mr-2" size="small" />
          <!-- Inline rename -->
          <template v-if="renamingFolder && renamingFolder.id === folder.id">
            <v-text-field
              v-model="renameValue"
              density="compact"
              variant="outlined"
              hide-details
              autofocus
              style="max-width: 260px"
              @click.stop
              @keydown.enter.stop="saveRename(folder)"
              @keydown.esc.stop="cancelRename()"
            />
            <v-btn size="small" color="success" class="ml-2" @click.stop="saveRename(folder)">Save</v-btn>
            <v-btn size="small" variant="text" @click.stop="cancelRename()">Cancel</v-btn>
          </template>
          <span v-else class="font-weight-medium">{{ folder.name }}</span>
          <span class="text-caption text-medium-emphasis ml-2">({{ modelsInFolder(folder.id).length }})</span>

          <template #actions>
            <v-tooltip location="top">
              <template #activator="{ props }">
                <v-btn :icon="mdiPencil" variant="text" size="small" v-bind="props" @click.stop="startRename(folder)" />
              </template>
              Rename folder
            </v-tooltip>
            <v-tooltip location="top">
              <template #activator="{ props }">
                <v-btn :icon="mdiDelete" variant="text" size="small" color="error" v-bind="props" @click.stop="confirmDeleteFolder(folder)" />
              </template>
              Delete folder
            </v-tooltip>
          </template>
        </v-expansion-panel-title>

        <v-expansion-panel-text class="pa-0">
          <v-data-table
            :headers="tableHeaders"
            :items="modelsInFolder(folder.id)"
            :items-per-page="-1"
            density="compact"
            hide-default-footer
            no-data-text="No trading models in this folder."
          >
            <template #item.description="{ item }">
              <div class="d-inline-flex align-center flex-wrap gap-1">
                <span>{{ item.description }}</span>
                <v-chip v-if="item.alert_enabled" size="x-small" color="primary">Alert</v-chip>
                <v-chip
                  v-for="user in (item.users_shared_with || [])"
                  :key="user.id"
                  size="x-small"
                  color="primary"
                  class="text-white"
                  label
                >{{ user.name }}</v-chip>
              </div>
            </template>
            <template #item.last_status_description="{ item }">
              <span :class="{ 'text-white bg-green-lighten-2 px-2 py-1 rounded': item.status === 2 }">
                {{ item.last_status_description }}
              </span>
            </template>
            <template #item.last_status_update="{ item }">
              {{ dateTime(item.last_status_update) }}
            </template>
            <template #item.actions="{ item }">
              <model-actions :model="item" :folders="folders" @action="handleAction" />
            </template>
          </v-data-table>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Unfiled models -->
    <v-card variant="outlined" class="mb-2">
      <v-card-title class="text-subtitle-2 d-flex align-center py-2 px-4">
        <v-icon :icon="mdiTrayFull" class="mr-2" size="small" />
        {{ sortedFolders.length ? 'Unfiled Models' : 'Your Whale Trading Models' }}
        <span class="text-caption text-medium-emphasis ml-2">({{ unfiledModels.length }})</span>
      </v-card-title>
      <v-data-table
        v-if="unfiledModels.length"
        :headers="tableHeaders"
        :items="unfiledModels"
        :items-per-page="-1"
        density="compact"
        hide-default-footer
      >
        <template #item.description="{ item }">
          <div class="d-inline-flex align-center flex-wrap gap-1">
            <span>{{ item.description }}</span>
            <v-chip v-if="item.alert_enabled" size="x-small" color="primary">Alert</v-chip>
            <v-chip
              v-for="user in (item.users_shared_with || [])"
              :key="user.id"
              size="x-small"
              color="primary"
              class="text-white"
              label
            >{{ user.name }}</v-chip>
          </div>
        </template>
        <template #item.last_status_description="{ item }">
          <span :class="{ 'text-white bg-green-lighten-2 px-2 py-1 rounded': item.status === 2 }">
            {{ item.last_status_description }}
          </span>
        </template>
        <template #item.last_status_update="{ item }">
          {{ dateTime(item.last_status_update) }}
        </template>
        <template #item.actions="{ item }">
          <model-actions :model="item" :folders="folders" @action="handleAction" />
        </template>
      </v-data-table>
      <v-card-text v-else class="font-italic text-medium-emphasis">
        {{ sortedFolders.length ? 'No unfiled trading models.' : "You haven't created any trading models yet." }}
      </v-card-text>
    </v-card>

    <!-- New folder dialog -->
    <v-dialog v-model="newFolderDialog" max-width="400" @keydown.esc="newFolderDialog = false">
      <v-card>
        <v-card-title>New Folder</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newFolderName"
            label="Folder name"
            variant="outlined"
            autofocus
            :rules="[v => !!v || 'Name is required']"
            @keydown.enter="createFolder()"
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="newFolderDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!newFolderName.trim() || saving" :loading="saving" @click="createFolder()">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete folder confirmation dialog -->
    <v-dialog v-model="deleteFolderDialog" max-width="420">
      <v-card>
        <v-card-title>Delete Folder</v-card-title>
        <v-card-text>
          Delete <strong>{{ folderToDelete && folderToDelete.name }}</strong>?
          Any models inside will be moved to Unfiled.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="deleteFolderDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="saving" @click="deleteFolder()">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/store/api'
import { useUserStore, useAppStore } from '@/store'
import FormattersMixin from '@/mixins/formatters'
import { mdiFolderPlus, mdiFolderOpen, mdiPencil, mdiDelete, mdiTrayFull } from '@mdi/js'
import ModelActions from './_ModelActions.vue'

export default {
  components: { ModelActions },
  mixins: [FormattersMixin],

  props: {
    models: { type: Array, default: () => [] },
    folders: { type: Array, default: () => [] }
  },

  emits: ['load-model', 'copy-model', 'delete-model', 'cancel-model', 'view-results', 'unshare-model'],

  data() {
    return {
      mdiFolderPlus, mdiFolderOpen, mdiPencil, mdiDelete, mdiTrayFull,
      tableHeaders: [
        { title: 'Name',        key: 'description',            sortable: true },
        { title: 'Last Status', key: 'last_status_description', sortable: true },
        { title: 'Last Run',    key: 'last_status_update',      sortable: true },
        { title: 'Annualized',  key: 'annualized_performance',  sortable: true },
        { title: 'YTD',         key: 'perf_ytd',                sortable: true },
        { title: 'All-time',    key: 'perf_all',                sortable: true },
        { title: '',            key: 'actions',                 sortable: false }
      ],
      openPanels: [],
      // New folder dialog
      newFolderDialog: false,
      newFolderName: '',
      // Rename folder
      renamingFolder: null,
      renameValue: '',
      // Delete folder
      deleteFolderDialog: false,
      folderToDelete: null,
      saving: false
    }
  },

  computed: {
    sortedFolders() {
      return [...this.folders].sort((a, b) => a.name.localeCompare(b.name))
    },
    unfiledModels() {
      return this.models.filter(m => !m.folder_id)
    }
  },

  methods: {
    modelsInFolder(folderId) {
      return this.models.filter(m => m.folder_id === folderId)
    },

    // ── Folder creation ────────────────────────────────────────────
    openNewFolderDialog() {
      this.newFolderName = ''
      this.newFolderDialog = true
    },

    async createFolder() {
      if (!this.newFolderName.trim() || this.saving) return
      this.saving = true
      try {
        const res = await api.post('trading_model_folders', { name: this.newFolderName.trim() })
        useUserStore().tradingModelFolders.push(res.data)
        this.openPanels.push(res.data.id)
        this.newFolderDialog = false
        this.newFolderName = ''
      } catch (e) {
        useAppStore().showNotification({ type: 'error', text: e.message })
      } finally {
        this.saving = false
      }
    },

    // ── Folder rename ──────────────────────────────────────────────
    startRename(folder) {
      this.renamingFolder = folder
      this.renameValue = folder.name
    },

    cancelRename() {
      this.renamingFolder = null
      this.renameValue = ''
    },

    async saveRename(folder) {
      if (!this.renameValue.trim()) return
      try {
        const res = await api.patch(`trading_model_folders/${folder.id}`, { name: this.renameValue.trim() })
        const store = useUserStore()
        const idx = store.tradingModelFolders.findIndex(f => f.id === folder.id)
        if (idx !== -1) store.tradingModelFolders[idx] = res.data
        this.cancelRename()
      } catch (e) {
        useAppStore().showNotification({ type: 'error', text: e.message })
      }
    },

    // ── Folder delete ──────────────────────────────────────────────
    confirmDeleteFolder(folder) {
      this.folderToDelete = folder
      this.deleteFolderDialog = true
    },

    async deleteFolder() {
      if (!this.folderToDelete || this.saving) return
      this.saving = true
      try {
        await api.delete(`trading_model_folders/${this.folderToDelete.id}`)
        const store = useUserStore()
        store.tradingModelFolders = store.tradingModelFolders.filter(f => f.id !== this.folderToDelete.id)
        // Unfile any models that were in this folder
        store.tradingModels.forEach(m => {
          if (m.folder_id === this.folderToDelete.id) m.folder_id = null
        })
        this.deleteFolderDialog = false
        this.folderToDelete = null
      } catch (e) {
        useAppStore().showNotification({ type: 'error', text: e.message })
      } finally {
        this.saving = false
      }
    },

    // ── Move model ─────────────────────────────────────────────────
    async moveModel(model, folderId) {
      try {
        await api.post('move_trading_model_to_folder', { id: model.id, folder_id: folderId })
        const store = useUserStore()
        const m = store.tradingModels.find(x => x.id === model.id)
        if (m) m.folder_id = folderId
      } catch (e) {
        useAppStore().showNotification({ type: 'error', text: e.message })
      }
    },

    // ── Dispatch model actions to parent ───────────────────────────
    handleAction({ type, model, folderId }) {
      switch (type) {
        case 'load':    this.$emit('load-model', model); break
        case 'copy':    this.$emit('copy-model', model); break
        case 'view':    this.$emit('view-results', model); break
        case 'cancel':  this.$emit('cancel-model', model); break
        case 'delete':  this.$emit('delete-model', model); break
        case 'unshare': this.$emit('unshare-model', model); break
        case 'move':    this.moveModel(model, folderId); break
      }
    }
  }
}
</script>
